import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';
import founderImg from '../assets/founder.png';
import founderHover from '../assets/founder-hover.jpeg';

const checks = [
    'We reply within 24 hours',
    'Free consultation',
    'No obligation booking',
];

const ContactPage = () => {
    const [formData, setFormData] = useState({ email: '', name: '', text: '' });
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [socials, setSocials] = useState(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        axios.get('https://kyrgyz-adventure-travel.com/api/v1/tours/contacts')
            .then(r => { if (r.data.results.length > 0) setSocials(r.data.results[0]); })
            .catch(e => console.error(e));
    }, []);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
            { threshold: 0.1 }
        );
        ref.current?.querySelectorAll('.reveal').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? match[2] : null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const csrfToken = getCookie('csrftoken');
        if (!csrfToken) { setMessage('CSRF token not found!'); return; }
        setSending(true);
        try {
            const r = await axios.post('https://kyrgyz-adventure-travel.com/api/v1/tours/lead/', formData, {
                headers: { 'accept': 'application/json', 'Content-Type': 'application/json', 'X-CSRFTOKEN': csrfToken }
            });
            if (r.status === 201) {
                setMessage('Message sent successfully!');
                setFormData({ email: '', name: '', text: '' });
            } else {
                setMessage('Error sending message. Please try again.');
            }
        } catch {
            setMessage('Error sending message. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const phone = socials?.whatsapp?.match(/(\d+)$/)?.[1];
    const instaHandle = socials?.instagram
        ?.replace(/https?:\/\/(www\.)?instagram\.com\//, '@')
        ?.replace(/\/$/, '');

    return (
        <>
            <div className="bg-dark min-h-screen">
                <Header isScrolled={isScrolled} />
                <div className="h-32" />

                <div ref={ref} className="container pb-24">

                    {/* Page header */}
                    <div className="reveal flex flex-col gap-4 mb-16">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-px bg-it-green opacity-90" />
                            <p className="font-unbounded text-it-green text-xs uppercase tracking-widest">Get in touch</p>
                        </div>
                        <h1 className="font-unbounded font-bold text-4xl md:text-5xl text-white leading-tight">
                            Plan Your Trip <span className="text-gold">With Us</span>
                        </h1>
                        <p className="font-manrope text-white/55 text-base md:text-lg max-w-xl leading-relaxed">
                            Get in touch with us and we'll start planning your perfect journey through Central Asia.
                        </p>
                        <div className="w-full h-px bg-white/10 mt-2" />
                    </div>

                    <div className="flex flex-col md:flex-row gap-12 md:gap-16 items-start">

                        {/* Left — Angela */}
                        <div className="reveal w-full md:w-2/5 flex flex-col gap-8" style={{ transitionDelay: '0.1s' }}>

                            {/* Photo with hover */}
                            <div className="relative rounded-3xl overflow-hidden group w-full max-w-xs">
                                <img
                                    src={founderImg}
                                    className="w-full object-cover transition-opacity duration-500 group-hover:opacity-0"
                                    alt="Angela"
                                />
                                <img
                                    src={founderHover}
                                    className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                                    alt="Angela"
                                />
                            </div>

                            {/* Intro */}
                            <div className="flex flex-col gap-2">
                                <p className="font-unbounded text-white font-bold text-sm">
                                    Hi, I'm Angela —
                                </p>
                                <p className="font-manrope text-white/60 text-sm leading-relaxed">
                                    your local guide in Kyrgyzstan. I'll help you create a unique and unforgettable journey.
                                </p>
                            </div>

                            {/* Direct contacts */}
                            {socials && (
                                <div className="flex flex-col gap-4">
                                    <p className="font-unbounded text-white/40 text-xs uppercase tracking-widest">
                                        Contact me directly
                                    </p>

                                    {/* WhatsApp */}
                                    <a href={socials.whatsapp} target="_blank" rel="noopener noreferrer"
                                       className="flex items-center gap-3 group/link">
                                        <span className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover/link:border-gold/40 transition-colors duration-200">
                                            <svg width="15" height="15" viewBox="0 0 26 27" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white/50 group-hover/link:text-gold transition-colors duration-200">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M18.957 15.6555C18.6327 15.4933 17.0423 14.7118 16.7462 14.6029C16.4501 14.4951 16.2346 14.4418 16.018 14.7662C15.8024 15.0884 15.183 15.8177 14.9947 16.0333C14.8053 16.2499 14.617 16.276 14.2937 16.1149C13.9704 15.9516 12.9276 15.6109 11.6921 14.5093C10.7309 13.6515 10.081 12.5924 9.89271 12.268C9.70439 11.9447 9.87203 11.7694 10.0342 11.6083C10.1801 11.4635 10.3575 11.2306 10.5197 11.0423C10.6819 10.8529 10.7352 10.7179 10.843 10.5013C10.9519 10.2857 10.8974 10.0974 10.7352 9.77302L10.0887 8.18047C9.81869 7.53279 9.55635 6.90252 9.09154 6.97763C8.90213 6.96892 8.6866 6.96674 8.47107 6.96674C8.25554 6.96674 7.90503 7.0473 7.60894 7.37168C7.31177 7.69498 6.47685 8.47765 6.47685 10.0702C6.47685 11.6616 7.63507 13.1998 7.79726 13.4164C7.95945 13.6319 10.0778 16.8997 13.3227 18.3007C14.0956 18.6338 14.6976 18.833 15.1667 18.981C15.9418 19.2281 16.6471 19.1933 17.2045 19.1095C17.825 19.017 19.1181 18.3268 19.3881 17.5714C19.657 16.8159 19.657 16.1682 19.5764 16.0333C19.4959 15.8983 19.2803 15.8177 18.9559 15.6555H18.957ZM13.0549 23.714H13.0506C11.1232 23.7144 9.23128 23.1963 7.57302 22.214L7.18114 21.9811L3.1078 23.05L4.19526 19.079L3.93945 18.6719C2.86194 16.9567 2.29171 14.9717 2.29466 12.9461C2.29683 7.01355 7.12345 2.18694 13.0593 2.18694C15.9331 2.18694 18.6348 3.30814 20.6661 5.34154C21.6679 6.33928 22.462 7.52578 23.0024 8.8324C23.5427 10.139 23.8186 11.5398 23.8141 12.9538C23.812 18.8863 18.9853 23.714 13.0549 23.714ZM22.2118 3.7969C21.0124 2.58961 19.5854 1.63236 18.0134 0.980608C16.4413 0.328855 14.7556 -0.00443719 13.0539 4.4606e-05C5.91952 4.4606e-05 0.111032 5.80744 0.108855 12.945C0.105549 15.2166 0.701432 17.4488 1.83638 19.4165L0 26.1252L6.8622 24.3247C8.76049 25.359 10.8877 25.9009 13.0495 25.9009H13.0549C20.1893 25.9009 25.9978 20.0935 25.9999 12.9548C26.0052 11.2537 25.6731 9.56848 25.0229 7.99655C24.3727 6.42461 23.4172 4.9972 22.2118 3.7969Z" fill="currentColor"/>
                                            </svg>
                                        </span>
                                        <span className="font-manrope text-white/70 text-sm group-hover/link:text-gold transition-colors duration-200">
                                            {phone ? `+${phone}` : 'WhatsApp'}
                                        </span>
                                    </a>

                                    {/* Instagram */}
                                    <a href={socials.instagram} target="_blank" rel="noopener noreferrer"
                                       className="flex items-center gap-3 group/link">
                                        <span className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover/link:border-gold/40 transition-colors duration-200">
                                            <svg width="15" height="15" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white/50 group-hover/link:text-gold transition-colors duration-200">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M7.64045 0.078C9.02673 0.0141818 9.46873 0 13 0C16.5313 0 16.9733 0.0153636 18.3584 0.078C19.7435 0.140636 20.6889 0.361636 21.5162 0.681909C22.3825 1.00927 23.1684 1.521 23.8184 2.18282C24.4802 2.83164 24.9907 3.61636 25.3169 4.48382C25.6384 5.31109 25.8582 6.25655 25.922 7.63927C25.9858 9.02791 26 9.46991 26 13C26 16.5313 25.9846 16.9733 25.922 18.3595C25.8594 19.7423 25.6384 20.6877 25.3169 21.515C24.9907 22.3826 24.4793 23.1686 23.8184 23.8184C23.1684 24.4802 22.3825 24.9907 21.5162 25.3169C20.6889 25.6384 19.7435 25.8582 18.3607 25.922C16.9733 25.9858 16.5313 26 13 26C9.46873 26 9.02673 25.9846 7.64045 25.922C6.25773 25.8594 5.31227 25.6384 4.485 25.3169C3.61745 24.9907 2.83142 24.4793 2.18164 23.8184C1.52027 23.1692 1.00846 22.3835 0.681909 21.5162C0.361636 20.6889 0.141818 19.7435 0.078 18.3607C0.0141818 16.9721 0 16.5301 0 13C0 9.46873 0.0153636 9.02673 0.078 7.64164C0.140636 6.25655 0.361636 5.31109 0.681909 4.48382C1.00895 3.61646 1.52114 2.83082 2.18282 2.18164C2.83168 1.52041 3.61692 1.00861 4.48382 0.681909C5.31109 0.361636 6.25773 0.141818 7.64045 0.078ZM11.3395 17.0075C12.2669 17.3936 13.2994 17.4457 14.2609 17.1549C15.2224 16.8642 16.0531 16.2487 16.6112 15.4136C17.1693 14.5784 17.4201 13.5754 17.3208 12.5759C17.2215 11.5763 16.7783 10.6422 16.0668 9.93318C15.6133 9.47993 15.0649 9.13287 14.4611 8.91699C13.8574 8.70111 13.2132 8.62179 12.5751 8.68473C11.937 8.74767 11.3208 8.9513 10.7709 9.28097C10.2209 9.61064 9.75088 10.0582 9.39463 10.5913C9.03838 11.1244 8.80477 11.7299 8.71061 12.3641C8.61645 12.9984 8.66409 13.6456 8.85009 14.2592C9.0361 14.8729 9.35584 15.4376 9.78631 15.9129C10.2168 16.3881 10.7472 16.7619 11.3395 17.0075ZM8.27509 8.27509C8.89557 7.65461 9.6322 7.16241 10.4429 6.82661C11.2536 6.49081 12.1225 6.31797 13 6.31797C13.8775 6.31797 14.7464 6.4908 15.5571 6.82661C16.3678 7.16241 17.1044 7.65461 17.7249 8.27509C18.3454 8.89557 18.8376 9.6322 19.1734 10.4429C19.5092 11.2536 19.682 12.1225 19.682 13C19.682 13.8775 19.5092 14.7464 19.1734 15.5571C18.8376 16.3678 18.3454 17.1044 17.7249 17.7249C16.4718 18.978 14.7722 19.682 13 19.682C11.2278 19.682 9.52821 18.978 8.27509 17.7249C7.02197 16.4718 6.31797 14.7722 6.31797 13C6.31797 11.2278 7.02197 9.52821 8.27509 8.27509Z" fill="currentColor"/>
                                            </svg>
                                        </span>
                                        <span className="font-manrope text-white/70 text-sm group-hover/link:text-gold transition-colors duration-200">
                                            {instaHandle || 'Instagram'}
                                        </span>
                                    </a>

                                    {/* Email */}
                                    <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${socials.email}`} target="_blank" rel="noopener noreferrer"
                                       className="flex items-center gap-3 group/link">
                                        <span className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover/link:border-gold/40 transition-colors duration-200">
                                            <svg width="15" height="12" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white/50 group-hover/link:text-gold transition-colors duration-200">
                                                <path d="M17.8124 12.2592C17.7251 12.2592 17.6379 12.2272 17.5697 12.1627L13.4878 8.33332C13.3453 8.19996 13.3382 7.97508 13.4722 7.83249C13.6063 7.68848 13.8298 7.68209 13.9738 7.81688L18.0557 11.6462C18.1983 11.7796 18.2054 12.0045 18.0713 12.1471C18.0011 12.2216 17.9074 12.2592 17.8124 12.2592Z" fill="currentColor" stroke="currentColor" strokeWidth="1.2"/>
                                                <path d="M4.18742 12.2593C4.09307 12.2593 3.99872 12.2217 3.92849 12.1472C3.79441 12.0047 3.80151 11.7798 3.9441 11.6464L8.02742 7.81705C8.17072 7.68368 8.3956 7.68936 8.52897 7.83265C8.66304 7.97524 8.65595 8.20012 8.51336 8.33349L4.43004 12.1628C4.36193 12.2274 4.27468 12.2593 4.18742 12.2593Z" fill="currentColor" stroke="currentColor" strokeWidth="1.2"/>
                                                <path d="M20.2265 15.8196H1.77351C0.79595 15.8196 0 15.0244 0 14.0461V1.95295C0 0.974684 0.79595 0.179443 1.77351 0.179443H20.2265C21.2041 0.179443 22 0.974684 22 1.95295V14.0461C22 15.0244 21.2041 15.8196 20.2265 15.8196ZM1.77351 0.888846C1.18683 0.888846 0.709403 1.36627 0.709403 1.95295V14.0461C0.709403 14.6328 1.18683 15.1102 1.77351 15.1102H20.2265C20.8132 15.1102 21.2906 14.6328 21.2906 14.0461V1.95295C21.2906 1.36627 20.8132 0.888846 20.2265 0.888846H1.77351Z" fill="currentColor" stroke="currentColor" strokeWidth="1.2"/>
                                                <path d="M10.9999 9.78186C10.5281 9.78186 10.0564 9.62508 9.69741 9.31224L0.588675 1.36622C0.441119 1.23781 0.425512 1.01364 0.553914 0.865378C0.683735 0.717822 0.907196 0.704343 1.05475 0.831326L10.1635 8.77664C10.6168 9.17177 11.3829 9.17177 11.8355 8.77664L20.9329 0.844096C21.0798 0.715694 21.3032 0.729882 21.4338 0.878147C21.5622 1.02641 21.5473 1.24987 21.3997 1.37899L12.3023 9.31153C11.9427 9.62508 11.4716 9.78186 10.9999 9.78186Z" fill="currentColor" stroke="currentColor" strokeWidth="1.2"/>
                                            </svg>
                                        </span>
                                        <span className="font-manrope text-white/70 text-sm group-hover/link:text-gold transition-colors duration-200">
                                            {socials.email}
                                        </span>
                                    </a>
                                </div>
                            )}

                            {/* Checklist */}
                            <div className="flex flex-col gap-3 p-6 rounded-2xl bg-white/3 border border-white/8">
                                {checks.map((item, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <span className="w-5 h-5 rounded-full bg-it-green/20 border border-it-green/50 flex items-center justify-center flex-shrink-0">
                                            <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                                                <path d="M1 3.5l2 2L8 1" stroke="#009246" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </span>
                                        <span className="font-manrope text-white/65 text-sm">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right — Form */}
                        <div className="reveal w-full md:w-3/5 flex flex-col gap-6" style={{ transitionDelay: '0.2s' }}>
                            <div className="flex flex-col gap-2">
                                <p className="font-unbounded text-white/40 text-xs uppercase tracking-widest">Or send a message</p>
                                <p className="font-manrope text-white/55 text-sm leading-relaxed">
                                    Tell us your travel dates, group size, and what kind of experience you're looking for.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                                <input
                                    type="text" name="name" placeholder="Your name"
                                    className="input-dark" value={formData.name} onChange={handleChange} required
                                />
                                <input
                                    type="email" name="email" placeholder="Your email"
                                    className="input-dark" value={formData.email} onChange={handleChange} required
                                />
                                <textarea
                                    name="text"
                                    placeholder="Travel dates, group size, type of experience..."
                                    rows={7}
                                    className="input-dark resize-none"
                                    value={formData.text}
                                    onChange={handleChange}
                                    required
                                />
                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="btn-gold self-start mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {sending ? 'Sending...' : 'Send message'}
                                </button>
                                {message && (
                                    <p className={`font-manrope text-sm ${message.includes('successfully') ? 'text-gold' : 'text-red-400'}`}>
                                        {message}
                                    </p>
                                )}
                            </form>

                            <p className="font-manrope text-white/35 text-xs">
                                We usually reply within 24 hours.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default ContactPage;
